"use client";

import { useCallback, useEffect, useRef, useState } from "react";

import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";

import { v4 as uuidv4 } from "uuid";

import { useAuth } from "@/contexts/AuthContext";
import type { BrowseMenuNode } from "@/lib/browse-menu-tree";
import { buildFeaturedSearchChatData, getFeaturedEditorialItem } from "@/lib/editorial-config";
import { browseByMenu, navigateToAgentResults } from "@/lib/results-navigation";
import { BRANDS_PATH, BRAND_EXPLORER_PATH } from "@/lib/route-paths";
import { colors, getTextStyle } from "@/styles/design-tokens";

import { DESKTOP_NAVBAR_MEGA_MENU_TOP_PX } from "../layout-constants";

interface TaxonomyMegaMenuProps {
  isOpen: boolean;
  rootNode: BrowseMenuNode;
  departments: BrowseMenuNode[];
  onClose?: () => void;
}

export function TaxonomyMegaMenu({
  isOpen,
  rootNode,
  departments,
  onClose,
}: TaxonomyMegaMenuProps) {
  const router = useRouter();
  const { userId } = useAuth();
  const [activeDepartment, setActiveDepartment] = useState<BrowseMenuNode | null>(null);
  const [isShopAllActive, setIsShopAllActive] = useState(false);
  const [shouldPinLayer1Actions, setShouldPinLayer1Actions] = useState(false);
  const [showLayer2SeeMore, setShowLayer2SeeMore] = useState(false);

  const mousePosRef = useRef<{ x: number; y: number }[]>([]);
  const switchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const layer1ContentRef = useRef<HTMLDivElement | null>(null);
  const layer1ListRef = useRef<HTMLDivElement | null>(null);
  const layer1ActionsRef = useRef<HTMLDivElement | null>(null);
  const layer2ListRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    return () => {
      if (switchTimeoutRef.current) clearTimeout(switchTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (isOpen) return;

    if (switchTimeoutRef.current) {
      clearTimeout(switchTimeoutRef.current);
      switchTimeoutRef.current = null;
    }

    mousePosRef.current = [];
    setActiveDepartment(null);
    setIsShopAllActive(false);
    setShouldPinLayer1Actions(false);
    setShowLayer2SeeMore(false);

    if (layer2ListRef.current) {
      layer2ListRef.current.scrollTop = 0;
    }
  }, [isOpen]);

  const updateLayer2OverflowState = useCallback(() => {
    const listEl = layer2ListRef.current;
    if (!listEl) return;

    const hasOverflow = listEl.scrollHeight > listEl.clientHeight + 1;
    const canScrollFurther = listEl.scrollTop + listEl.clientHeight < listEl.scrollHeight - 1;

    setShowLayer2SeeMore(hasOverflow && canScrollFurther);
  }, []);

  const updateLayer1OverflowState = useCallback(() => {
    const contentEl = layer1ContentRef.current;
    const listEl = layer1ListRef.current;
    const actionsEl = layer1ActionsRef.current;

    if (!contentEl || !listEl || !actionsEl) {
      setShouldPinLayer1Actions(false);
      return;
    }

    const availableHeight = contentEl.clientHeight;
    const requiredHeight = listEl.scrollHeight + actionsEl.offsetHeight + 16;

    setShouldPinLayer1Actions(requiredHeight > availableHeight + 1);
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const rafId = window.requestAnimationFrame(updateLayer1OverflowState);
    return () => window.cancelAnimationFrame(rafId);
  }, [departments, isOpen, updateLayer1OverflowState]);

  useEffect(() => {
    if (!activeDepartment?.children_nodes?.length) {
      setShowLayer2SeeMore(false);
      return;
    }

    const listEl = layer2ListRef.current;
    if (listEl) listEl.scrollTop = 0;
    const rafId = window.requestAnimationFrame(updateLayer2OverflowState);

    return () => window.cancelAnimationFrame(rafId);
  }, [activeDepartment, updateLayer2OverflowState]);

  useEffect(() => {
    if (!isOpen) return;

    let resizeTimerId: ReturnType<typeof setTimeout> | undefined;
    const handleResize = () => {
      if (resizeTimerId) clearTimeout(resizeTimerId);
      resizeTimerId = setTimeout(() => {
        updateLayer1OverflowState();
        updateLayer2OverflowState();
      }, 100);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      if (resizeTimerId) clearTimeout(resizeTimerId);
      window.removeEventListener("resize", handleResize);
    };
  }, [isOpen, updateLayer1OverflowState, updateLayer2OverflowState]);

  const handleColumnMouseMove = (e: React.MouseEvent) => {
    mousePosRef.current.push({ x: e.clientX, y: e.clientY });
    if (mousePosRef.current.length > 3) mousePosRef.current.shift();
  };

  const handleSubmenuEnter = () => {
    if (switchTimeoutRef.current) {
      clearTimeout(switchTimeoutRef.current);
      switchTimeoutRef.current = null;
    }
  };

  const handleDepartmentHover = (dept: BrowseMenuNode) => {
    if (switchTimeoutRef.current) {
      clearTimeout(switchTimeoutRef.current);
      switchTimeoutRef.current = null;
    }

    if (!activeDepartment || activeDepartment.uuid === dept.uuid) {
      setActiveDepartment(dept);
      setIsShopAllActive(false);
      return;
    }

    const positions = mousePosRef.current;
    if (positions.length >= 2) {
      const prev = positions[positions.length - 2];
      const curr = positions[positions.length - 1];
      const dx = curr.x - prev.x;
      const dy = Math.abs(curr.y - prev.y);
      if (dx > 0 && dx > dy) {
        switchTimeoutRef.current = setTimeout(() => {
          setActiveDepartment(dept);
          setIsShopAllActive(false);
        }, 300);
        return;
      }
    }

    setActiveDepartment(dept);
    setIsShopAllActive(false);
  };

  const editorialFeatured = getFeaturedEditorialItem(rootNode.display_name);
  const featuredImageStyle =
    editorialFeatured && isOpen
      ? {
          backgroundImage: `url(${editorialFeatured.image})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }
      : null;

  const handleBrowseNavigate = (node: BrowseMenuNode) => {
    const threadId = uuidv4();
    browseByMenu(router, threadId, {
      facets: node.search_query.facets || [],
      display_path: node.display_path,
      navbar_category: node.display_path[0] || rootNode.display_name,
    });
    setIsShopAllActive(false);
    onClose?.();
  };

  const handleFeaturedClick = () => {
    if (!editorialFeatured) return;

    const chatId = uuidv4();
    const chatData = buildFeaturedSearchChatData(
      rootNode.display_name,
      editorialFeatured.label,
      userId ?? undefined
    );
    sessionStorage.setItem(`chat-${chatId}`, JSON.stringify(chatData));
    navigateToAgentResults(router, chatId);
    onClose?.();
  };

  const handleDepartmentClick = (dept: BrowseMenuNode) => {
    if (dept.children_nodes && dept.children_nodes.length > 0) {
      setActiveDepartment(dept);
    } else {
      setActiveDepartment(null);
    }
    setIsShopAllActive(false);
    handleBrowseNavigate(dept);
  };

  const handleShopByBrandClick = () => {
    router.push(BRAND_EXPLORER_PATH);
    onClose?.();
  };

  const handleLayer2SeeMoreClick = () => {
    const listEl = layer2ListRef.current;
    if (!listEl) return;

    listEl.scrollBy({
      top: Math.max(120, listEl.clientHeight * 0.75),
      behavior: "smooth",
    });
  };

  const hiddenPanelStyles = {
    opacity: isOpen ? 1 : 0,
    visibility: isOpen ? "visible" : "hidden",
    pointerEvents: isOpen ? "auto" : "none",
  } as const;
  const megaMenuTop = `${DESKTOP_NAVBAR_MEGA_MENU_TOP_PX}px`;

  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0"
        aria-hidden={!isOpen}
        data-testid={isOpen ? "taxonomy-mega-menu-overlay" : undefined}
        style={{
          top: megaMenuTop,
          backgroundColor: "rgba(0, 0, 0, 0.05)",
          zIndex: 99998,
          ...hiddenPanelStyles,
        }}
        onClick={onClose}
      />

      <div
        className="fixed bg-white"
        aria-hidden={!isOpen}
        data-testid={isOpen ? "taxonomy-mega-menu" : undefined}
        style={{
          top: megaMenuTop,
          left: "0",
          right: "0",
          width: "100vw",
          height: "520px",
          borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
          zIndex: 99999,
          boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
          ...hiddenPanelStyles,
        }}
      >
        <div
          className="grid grid-cols-3"
          style={{
            maxWidth: "1280px",
            paddingTop: "42px",
            paddingBottom: "42px",
            paddingLeft: "calc(30px - 0.37px)",
            paddingRight: "30px",
            gap: "42px",
            height: "100%",
          }}
        >
          <div
            className="flex min-h-0 flex-col"
            data-testid={isOpen ? "taxonomy-layer1-column" : undefined}
            style={{ gap: "21px" }}
            onMouseMove={handleColumnMouseMove}
          >
            <div
              style={{
                height: "25.5px",
                borderBottom: "1px solid rgba(0, 0, 0, 0.2)",
                paddingBottom: "4px",
              }}
            >
              <span
                style={{
                  ...getTextStyle("linkPrimaryActive"),
                  color: colors.text.black,
                }}
              >
                Shop {rootNode.display_name}
              </span>
            </div>

            <div
              ref={layer1ContentRef}
              className={shouldPinLayer1Actions ? "flex min-h-0 flex-1 flex-col" : "flex flex-col"}
            >
              <div
                ref={layer1ListRef}
                className={
                  shouldPinLayer1Actions
                    ? "flex min-h-0 flex-1 flex-col overflow-y-auto pr-2 [&::-webkit-scrollbar]:hidden"
                    : "flex flex-col pr-2"
                }
                style={{
                  gap: "16px",
                  ...(shouldPinLayer1Actions
                    ? {
                        scrollbarWidth: "none",
                        msOverflowStyle: "none",
                      }
                    : {}),
                }}
              >
                {departments.map((dept) => (
                  <button
                    key={dept.uuid}
                    onMouseEnter={() => handleDepartmentHover(dept)}
                    onClick={() => handleDepartmentClick(dept)}
                    className="flex cursor-pointer flex-col items-start overflow-visible p-0"
                    data-testid={isOpen ? "taxonomy-layer1-button" : undefined}
                    data-layer1-name={isOpen ? dept.display_name : undefined}
                    style={{ flexShrink: 0 }}
                  >
                    <div className="inline-flex flex-col" style={{ paddingBottom: "1.739px" }}>
                      <span
                        style={{
                          ...getTextStyle("linkPrimaryActive"),
                          color: colors.text.black,
                        }}
                      >
                        {dept.display_name}
                      </span>

                      <div
                        style={{
                          backgroundColor:
                            activeDepartment?.uuid === dept.uuid
                              ? colors.text.black
                              : "transparent",
                          height: "1.739px",
                          width: "100%",
                          marginTop: "2px",
                        }}
                      />
                    </div>
                  </button>
                ))}
              </div>

              <div
                ref={layer1ActionsRef}
                className={
                  shouldPinLayer1Actions ? "mt-4 flex flex-shrink-0 flex-col" : "mt-4 flex flex-col"
                }
                style={{ gap: "16px" }}
              >
                <button
                  onMouseEnter={() => {
                    if (switchTimeoutRef.current) {
                      clearTimeout(switchTimeoutRef.current);
                      switchTimeoutRef.current = null;
                    }
                    setActiveDepartment(null);
                    setIsShopAllActive(true);
                  }}
                  onClick={() => handleBrowseNavigate(rootNode)}
                  className="flex cursor-pointer flex-col items-start overflow-visible p-0"
                  style={{ flexShrink: 0 }}
                >
                  <div className="inline-flex flex-col" style={{ paddingBottom: "1.739px" }}>
                    <span
                      style={{
                        ...getTextStyle("linkPrimaryActive"),
                        color: colors.text.black,
                      }}
                    >
                      All {rootNode.display_name}
                    </span>
                    <div
                      style={{
                        backgroundColor: isShopAllActive ? colors.text.black : "transparent",
                        height: "1.739px",
                        width: "100%",
                        marginTop: "2px",
                      }}
                    />
                  </div>
                </button>

                <div className="mt-2 flex flex-col" style={{ gap: "8px" }}>
                  <Link
                    href={BRANDS_PATH}
                    onMouseEnter={() => {
                      if (switchTimeoutRef.current) {
                        clearTimeout(switchTimeoutRef.current);
                        switchTimeoutRef.current = null;
                      }
                      setActiveDepartment(null);
                      setIsShopAllActive(false);
                    }}
                    onClick={() => onClose?.()}
                    className="group inline-flex flex-col items-start"
                    data-testid={isOpen ? "taxonomy-brands-link" : undefined}
                  >
                    <div className="inline-flex flex-col" style={{ paddingBottom: "1.739px" }}>
                      <span
                        style={{
                          ...getTextStyle("linkPrimaryActive"),
                          color: colors.text.black,
                        }}
                      >
                        Browse by brand
                      </span>
                      <div className="mt-0.5 h-[1.739px] w-full bg-transparent group-hover:bg-black" />
                    </div>
                  </Link>

                  <button
                    onMouseEnter={() => {
                      if (switchTimeoutRef.current) {
                        clearTimeout(switchTimeoutRef.current);
                        switchTimeoutRef.current = null;
                      }
                      setActiveDepartment(null);
                      setIsShopAllActive(false);
                    }}
                    onClick={handleShopByBrandClick}
                    className="inline-flex cursor-pointer items-start p-0 text-left transition-opacity hover:opacity-80"
                    style={{ flexShrink: 0 }}
                  >
                    <span
                      className="text-[13px] leading-5"
                      style={{
                        color: colors.text.grayLightest,
                      }}
                    >
                      Need ideas?{" "}
                      <span
                        style={{
                          color: colors.text.black,
                        }}
                      >
                        Try our{" "}
                        <span className="underline decoration-[1.5px] underline-offset-4">
                          Brand Discovery
                        </span>{" "}
                        tool
                      </span>
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div
            className="flex min-h-0 flex-col"
            style={{ gap: "21px" }}
            onMouseEnter={handleSubmenuEnter}
          >
            <div style={{ height: "25.5px" }} />
            <div className="relative min-h-0 flex-1">
              {activeDepartment?.children_nodes && (
                <div
                  ref={layer2ListRef}
                  onScroll={updateLayer2OverflowState}
                  className="flex h-full min-h-0 flex-col items-start overflow-y-auto overflow-x-hidden pb-8 pr-2 [&::-webkit-scrollbar]:hidden"
                  data-testid={isOpen ? "taxonomy-layer2-list" : undefined}
                  style={{
                    gap: "10.5px",
                    scrollbarWidth: "none",
                    msOverflowStyle: "none",
                  }}
                >
                  {activeDepartment.children_nodes.map((sub) => (
                    <button
                      key={sub.uuid}
                      onClick={() => handleBrowseNavigate(sub)}
                      className="group inline-flex flex-col items-start"
                      data-testid={isOpen ? "taxonomy-layer2-item" : undefined}
                      data-layer2-name={isOpen ? sub.display_name : undefined}
                      style={{ paddingBottom: "1.739px" }}
                    >
                      <span
                        style={{
                          ...getTextStyle("linkPrimaryInactive"),
                          color: "#4A5565",
                        }}
                      >
                        {sub.display_name}
                      </span>
                      <div className="mt-0.5 h-[1.739px] w-full bg-transparent group-hover:bg-[#4A5565]" />
                    </button>
                  ))}
                  <button
                    onClick={() => handleBrowseNavigate(activeDepartment)}
                    className="group inline-flex flex-col items-start"
                    data-testid={isOpen ? "taxonomy-layer2-item" : undefined}
                    data-layer2-name={isOpen ? `All ${activeDepartment.display_name}` : undefined}
                    style={{ paddingBottom: "1.739px" }}
                  >
                    <span
                      style={{
                        ...getTextStyle("linkPrimaryInactive"),
                        color: "#4A5565",
                      }}
                    >
                      All {activeDepartment.display_name}
                    </span>
                    <div className="mt-0.5 h-[1.739px] w-full bg-transparent group-hover:bg-[#4A5565]" />
                  </button>
                </div>
              )}
              {showLayer2SeeMore && (
                <div className="pointer-events-none absolute inset-x-0 bottom-0 flex justify-start bg-gradient-to-t from-white via-white/95 to-transparent pb-1 pt-6">
                  <button
                    onClick={handleLayer2SeeMoreClick}
                    className="pointer-events-auto inline-flex items-center transition-opacity hover:opacity-80"
                    data-testid={isOpen ? "taxonomy-layer2-see-more" : undefined}
                    aria-label="See more subcategories"
                  >
                    <span
                      style={{
                        ...getTextStyle("linkPrimaryInactive"),
                        color: "#4A5565",
                      }}
                    >
                      See more
                    </span>
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col" style={{ gap: "21px" }}>
            <div
              style={{
                height: "25.5px",
                borderBottom: "1px solid rgba(0, 0, 0, 0.2)",
                paddingBottom: "4px",
              }}
            >
              <span
                style={{
                  ...getTextStyle("linkPrimaryActive"),
                  color: colors.text.black,
                }}
              >
                Featured
              </span>
            </div>

            {editorialFeatured ? (
              <>
                <div
                  style={{
                    width: "100%",
                    height: "337px",
                    backgroundColor: "#D9D9D9",
                    ...(featuredImageStyle || {}),
                  }}
                />

                <div className="flex flex-col" style={{ gap: "10.5px" }}>
                  <span
                    style={{
                      ...getTextStyle("heading3"),
                      color: colors.text.black,
                    }}
                  >
                    {editorialFeatured.label}
                  </span>
                  <button
                    onClick={handleFeaturedClick}
                    className="flex items-center transition-opacity hover:opacity-80"
                    style={{ gap: "15px" }}
                  >
                    <span
                      style={{
                        ...getTextStyle("heading4"),
                        color: "#4A5565",
                      }}
                    >
                      SHOP NOW
                    </span>
                    <Image src="/icons/arrow.svg" alt="" width={12.25} height={12.25} />
                  </button>
                </div>
              </>
            ) : (
              <div className="flex flex-col" style={{ gap: "10.5px" }}>
                <span
                  style={{
                    ...getTextStyle("heading3"),
                    color: colors.text.black,
                  }}
                >
                  Shop {rootNode.display_name}
                </span>
                <button
                  onClick={() => handleBrowseNavigate(rootNode)}
                  className="flex items-center transition-opacity hover:opacity-80"
                  style={{ gap: "15px" }}
                >
                  <span
                    style={{
                      ...getTextStyle("heading4"),
                      color: "#4A5565",
                    }}
                  >
                    SHOP NOW
                  </span>
                  <Image src="/icons/arrow.svg" alt="" width={12.25} height={12.25} />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
